import React, { useCallback, useEffect } from "react";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axios from "axios";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import ApiService from "services/api.service";
import { ApiConfig } from "../../../constants";
import { delay } from "utils";

const enum Status {
  Paused = "Paused",
  InProgress = "InProgress",
  Ideal = "Ideal",
}

interface ProgressStatus {
  completedCount: number;
  waitingCount: number;
  failedCount: number;
  activeCount: number;
  status: Status;
}

const initialProgressStatus: ProgressStatus = {
  status: Status.Ideal,
  completedCount: 0,
  waitingCount: 0,
  failedCount: 0,
  activeCount: 0,
};

const INITIAL_POLLING_INTERVAL = 5000; // 10 seconds

export enum Process_Group {
  Segments = "segments",
  Journeys = "journeys",
}

interface OverallProgressStatusProps {
  trackedProcess?: Process_Group;
}

interface IUseTrackedProcessStatue {
  trackedProcess?: Process_Group;
}

const getTrackedProcessStatus = async (trackedProcess: Process_Group) => {
  let url: string;
  switch (trackedProcess) {
    case Process_Group.Segments:
      url = `${ApiConfig.segments}/status`;
      break;
    case Process_Group.Journeys:
      url = `${ApiConfig.journeys}/status`;
      break;
    default:
      url = `${ApiConfig.segments}/status`;
  }

  return ApiService.get({ url });
};

const useTrackedProcessStatue = ({
  trackedProcess = Process_Group.Segments,
}: IUseTrackedProcessStatue) => {
  const [progress, setProgress] = React.useState<ProgressStatus>(
    initialProgressStatus
  );
  const trackerProcessRef = React.useRef<any>(null);
  const fetchProgressStatus = async (delayInterval = 500): Promise<any> => {
    try {
      const { data, status } = await getTrackedProcessStatus(trackedProcess);
      if (status !== 200) {
        return await delay(delayInterval * 2);
      }
      if (data) {
        setProgress(data);
        return;
      }
    } catch (error) {
      console.error("Error fetching progress status:", error);
      clearInterval(trackerProcessRef.current);
    }
  };

  useEffect(() => {
    trackerProcessRef.current = setInterval(async () => {
      await fetchProgressStatus(INITIAL_POLLING_INTERVAL);
    }, INITIAL_POLLING_INTERVAL);
    return () => {
      clearInterval(trackerProcessRef.current);
    };
  }, [trackerProcessRef.current]);

  return { progress };
};

const OverallProgressStatus = ({
  trackedProcess = Process_Group.Segments,
}: OverallProgressStatusProps) => {
  const { progress } = useTrackedProcessStatue({ trackedProcess });

  const getStatusIcon = () => {
    const iconSize = 16; // Set a consistent icon size
    switch (progress.status) {
      case Status.Paused:
        return (
          <PauseCircleOutlineIcon
            style={{ fontSize: iconSize }}
            className="text-yellow-500"
          />
        );
      case Status.InProgress:
        return (
          <RefreshIcon
            style={{ fontSize: iconSize }}
            className="text-blue-500 animate-spin"
          />
        );
      case Status.Ideal:
        return (
          <PlayCircleOutlineIcon
            style={{ fontSize: iconSize }}
            className="text-gray-500"
          />
        );
      default:
        return null;
    }
  };
  const isIdeal = progress.status === Status.Ideal;
  const titleLabel =
    trackedProcess === Process_Group.Segments ? "Segments" : "Journeys";

  return (
    <div
      className="mb-5 p-4 border rounded-lg shadow-lg bg-white gap-2"
      style={{ position: "fixed", bottom: 0 }}
    >
      <div className="text-[10px] text-gray-600 flex justify-center items-center gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="h-[16px] w-[16px] "
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 6.878V6a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 18 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 0 0 4.5 9v.878m13.5-3A2.25 2.25 0 0 1 19.5 9v.878m0 0a2.246 2.246 0 0 0-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0 1 21 12v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6c0-.98.626-1.813 1.5-2.122"
          />
        </svg>
        {titleLabel} background processes
      </div>
      <div className="pl-5">
        <div className="flex items-center space-x-3 mb-3">
          {getStatusIcon()}
          <h2 className="text-xl font-semibold">{progress.status}</h2>
        </div>
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <CheckCircleOutlineIcon
              className="text-green-500"
              style={{ fontSize: 14 }}
            />
            <span className="text-gray-600 flex justify-between flex-1">
              <span>Completed:</span>
              <span>{progress.completedCount}</span>
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <ErrorOutlineIcon
              className="text-red-500"
              style={{ fontSize: 14 }}
            />
            <span className="text-gray-600 flex justify-between flex-1">
              <span>Failed:</span>
              <span>{progress.failedCount}</span>
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <InformationCircleIcon className="text-[orange] text-[14px] h-[14px]" />
            <span className="text-gray-600 flex justify-between flex-1">
              <span>Waiting:</span>
              <span>{progress.waitingCount}</span>
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <InformationCircleIcon className="text-gray-600 text-[14px] h-[14px]" />
            <span className="text-gray-600 flex justify-between flex-1">
              <span>Active:</span>
              <span>{progress.activeCount}</span>
            </span>
          </div>
        </div>
        {!isIdeal && (
          <div className="mt-4 bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full"
              style={{
                width: `${
                  (progress.completedCount /
                    (progress.completedCount +
                      progress.waitingCount +
                      progress.failedCount)) *
                  100
                }%`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OverallProgressStatus;
